import { ProDescriptions } from "@ant-design/pro-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, message, Popover, Input } from "antd";
import {
  BasicTable,
  RightDrawer,
  HView,
  AlarmsHeader,
  AlarmsReport,
  Status,
} from "@/components";
import { useState, useEffect, useMemo } from "react";
import { DEPLOYMENT, PageEnum } from "@/enums/pageEnum";
import { useSize } from "ahooks";
import { usePolling } from "@/hooks";
import { setTrapAcknowledge, liftTrapClosed } from "@api";
import { getTimeToString, getLiftNumberNA, getIsNumberNA } from "@/utils/toolt";

export default function PassengerTrapDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data, loading, fetchData } = usePolling({
    url: `/api/passenger/trap/${id}`,
  });
  const [teamName, setTeamName] = useState<string>("");
  const size = useSize(document.querySelector(".ant-drawer"));

  useEffect(() => {
    setTeamName(data?.team);
  }, [data?.team]);

  const tableHeight = useMemo(() => {
    const currentHeight = size?.height ? size.height - 824 : 0;
    return currentHeight < 150 ? 150 : currentHeight;
  }, [size]);

  const columns = useMemo(
    () => [
      {
        title: t("S/N"),
        dataIndex: "id",
        width: 50,
        renderText: (_: string, __: string, index: number) => `${index + 1}`,
      },
      {
        title: t("liftFaultNumber"),
        dataIndex: "alarmLiftFaultNumber",
        width: 130,
        renderText: (_: string, record: { description: string }) =>
          record?.description ? (
            <Popover content={<p>{record?.description}</p>} title="">
              {_}
            </Popover>
          ) : (
            _
          ),
      },
      {
        title: t("liftfaulttime"),
        dataIndex: "alarmStartTs",
        renderText: (_: number) => getTimeToString(_),
        width: 160,
      },
      {
        title: t("liftfaulcletime"),
        dataIndex: "alarmClearTs",
        renderText: (_: number) => getTimeToString(_),
        width: 160,
      },
      { title: t("liftalarmStatus"), dataIndex: "alarmStatus", width: 160 },
      { title: t("Duration"), dataIndex: "duration", width: 120 },
      {
        title: t("action"),
        dataIndex: "action",
        renderText: (_: string, record: { id: string }) => (
          <HView
            name={t("view")}
            path={`${PageEnum.ALARMLF}/detail/${record?.id}`}
            state={{ callUrl: PageEnum.ALARMLF }}
          />
        ),
      },
    ],
    [t]
  );

  const getReport = useMemo(() => {
    return (
      <AlarmsReport
        data={data}
        fieldsKeys={{
          name: "operatorName",
          code: "ptCode",
          result: "result",
        }}
        request={liftTrapClosed}
        onSubmit={() => {
          fetchData();
        }}
      />
    );
  }, [
    data?.operationalStatus,
    data?.reportAt,
    data?.acknowledgeAt,
    data?.arrivalTs,
    fetchData,
  ]);

  const getTeamInput = useMemo(() => {
    return (
      <Input
        value={teamName}
        onChange={(e) => {
          setTeamName(e.target.value);
        }}
        style={{ width: 200, marginLeft: 5 }}
        placeholder=""
        disabled={data?.operationalStatus !== DEPLOYMENT}
      />
    );
  }, [teamName, data?.operationalStatus]);

  return (
    <RightDrawer title={data?.liftName} loading={loading}>
      <AlarmsHeader data={data} />
      <ProDescriptions
        dataSource={data}
        className="faultInputCode"
        emptyText=" "
        columns={[
          {
            title: t("team"),
            dataIndex: "teamName",
            style: { width: 300 },
            children: getTeamInput,
          },
          {
            title: t("ptSettime"),
            dataIndex: "alarmStartTs",
            render: getLiftNumberNA,
          },
          {
            title: "",
            dataIndex: "address",
            className: "acknowledgeCalss",
            children: (
              <Button
                variant="outlined"
                color="primary"
                disabled={data?.operationalStatus !== DEPLOYMENT}
                onClick={() => {
                  setTrapAcknowledge({ id, team: teamName }).then(() => {
                    message.success(t("success"));
                    fetchData();
                  });
                }}
              >
                {t("acknowledge")}
              </Button>
            ),
          },
          {
            title: t("alarmStatus"),
            dataIndex: "alarmStatus",
            renderText: (_: string) => <Status status={_} />,
          },
          {
            title: t("ptclearTime"),
            dataIndex: "alarmClearTs",
            render: getLiftNumberNA,
          },
          {
            title: t("duration"),
            dataIndex: "duration",
            render: getIsNumberNA,
          },
        ]}
      />
      <div className="flex items-center justify-start py-4 border-t border-[#E8E9ED] dark:border-[#ffffff21] mt-6">
        <div className="w-[247px]">
          {t("liftfaultCount")}: {data?.liftFaultCnt}
        </div>
      </div>
      {data && id && (
        <BasicTable
          isShowCheckbox={false}
          columns={columns}
          isDefaultToolBar={false}
          isPagination={false}
          longPolling
          tableHeight={tableHeight}
          isAddPathParams={false}
          className="min-h-max !p-0 mb-5 liftFaultTable"
          requestUrl={`/api/lift/fault/page/by-pt/${id}`}
        />
      )}
      {getReport}
    </RightDrawer>
  );
}
